import {
    Children,
    createContext,
    DetailedHTMLProps,
    forwardRef,
    HTMLAttributes,
    isValidElement,
    PropsWithChildren,
    ReactNode,
    useContext,
} from 'react';
import classNames from 'classnames';

import styles from './Listbox.module.scss';

type DivProps = DetailedHTMLProps<
    HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
>;

type ListboxInnerProps = {
    variant?: 'list' | 'grid';
    direction?: 'vertical' | 'horizontal';
    selectedValues?: string[];
    onSelectionChange?: (selectedValues: Array<string>) => void;
    onSelected?: (key: string) => void;
    multiSelect?: boolean;
    spacing?: number;
};
const ListboxContext = createContext<ListboxInnerProps>({});

export type ListboxProps = PropsWithChildren<DivProps> & ListboxInnerProps;

export const Listbox = forwardRef<HTMLDivElement, ListboxProps>(
    function Listbox(
        {
            className,
            children,
            variant,
            multiSelect,
            selectedValues,
            direction = 'vertical',
            onSelectionChange,
            onSelected,
            spacing = 10,
            ...divProps
        },
        ref,
    ) {
        const hasSection = Children.toArray(children).some(
            (node) => isValidElement(node) && node.type === ListboxSection,
        );

        return (
            <ListboxContext.Provider
                value={{
                    variant,
                    multiSelect,
                    selectedValues,
                    onSelectionChange,
                    onSelected,
                    direction,
                    spacing,
                }}
            >
                <div
                    role="listbox"
                    ref={ref}
                    className={className}
                    {...divProps}
                >
                    {hasSection ? (
                        children
                    ) : (
                        <ListboxSection>{children}</ListboxSection>
                    )}
                </div>
            </ListboxContext.Provider>
        );
    },
);

export interface ListboxItemProps extends PropsWithChildren<DivProps> {
    value?: string;
    startContent?: ReactNode;
    endContent?: ReactNode;
}

export const ListboxItem = ({
    className,
    children,
    startContent,
    endContent,
    value,
    ...liProps
}: ListboxItemProps) => {
    const context = useContext(ListboxContext);

    const selectedValues =
        context.selectedValues === undefined
            ? []
            : Array.isArray(context.selectedValues)
              ? context.selectedValues
              : [context.selectedValues];
    const isSelected = value !== undefined && selectedValues.includes(value);

    return (
        <div
            role="option"
            className={classNames(className, styles.listItem, {
                [styles.listItemSelected]: isSelected,
            })}
            aria-selected={isSelected}
            onClick={() => {
                if (value === undefined) {
                    return;
                }
                if (context.onSelected) {
                    context.onSelected(value);
                }
                if (context.multiSelect) {
                    context.onSelectionChange?.([...selectedValues, value]);
                } else {
                    context.onSelectionChange?.([value]);
                }
            }}
            {...liProps}
        >
            {startContent && <div>{startContent}</div>}
            {children}
            {endContent && (
                <div className={styles.listItemEndContent}>{endContent}</div>
            )}
        </div>
    );
};

const ListboxLayout = ({ children }: PropsWithChildren) => {
    const { variant, direction, spacing } = useContext(ListboxContext);
    return (
        <div
            className={classNames(
                styles.list,
                variant === 'grid' && styles.listGrid,
                direction === 'horizontal' && styles.directionHorizontal,
                direction === 'vertical' && styles.directionVertical,
            )}
            style={{
                gap: spacing,
            }}
        >
            {children}
        </div>
    );
};

export interface ListboxSectionProps extends PropsWithChildren {
    header?: string;
}
export const ListboxSection = ({ header, children }: ListboxSectionProps) => {
    return (
        <section className={styles.listboxSection}>
            {header !== undefined && <p>{header}</p>}
            <ListboxLayout>{children}</ListboxLayout>
        </section>
    );
};
